import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { FeatureToggleSettings } from '../featuresToggleSettings.model';
import { EupRoutesService } from '@core/eupRoutes.service';
import { EupHttpHandler } from '@core/eupHttpHandler.service';
import { FeatureToggleStore } from '../state/feature-toggle-store';
import { FeatureToggleQuery } from '../state/feature-toggle-query';

@Injectable()
export class FeaturesToggleSettingsService {
	private featuresToggleSettings$: Observable<FeatureToggleSettings[]> = this.query.featureToggles$;

	private getAll(): Observable<FeatureToggleSettings[]> {
		return this.http.getWithoutCancel(this.eupRoutesService.getFeaturesToggleSettingsEndpoint(), {}, false, true);
	}

	constructor(
		private http: EupHttpHandler,
		private eupRoutesService: EupRoutesService,
		private store: FeatureToggleStore,
		private query: FeatureToggleQuery
	) {	}

	private async updateFeatureToggleSettings(featureToggleSettings: FeatureToggleSettings[], date: Date): Promise<void> {
		await this.store.update((state) => {
			state.featureToggles = featureToggleSettings;
			state.lastUpdate = date;
		});
	}
	
	async resetFeaturesToggles(): Promise<void> {
		await this.updateFeatureToggleSettings([], null);
	}

	getFeaturesToggleSettings(): Observable<FeatureToggleSettings[]> {
		// Check if data is already in the store
		if (this.query.isFeatureTogglesUpdated) {
			return this.featuresToggleSettings$;
		}

		// Otherwise, make an HTTP call and update the store
		return this.getAll().pipe(
			tap(async (res: FeatureToggleSettings[]) => {
				this.updateFeatureToggleSettings(res, new Date());
			}),
			shareReplay(1)
		);
	}

	isVisibleFeature(featureId: string, defaultValue = true): boolean {
		const { featureToggles: featuresToggleSettings } = this.query;
		const selectedFeature: FeatureToggleSettings = featuresToggleSettings.find(
			(feature: FeatureToggleSettings) => feature.id === featureId
		);

		return selectedFeature ? selectedFeature.isActive : defaultValue;
	}

	isVisibleFeature$(featureId: string): Observable<boolean> {
		return this.getFeaturesToggleSettings().pipe(map(() => this.isVisibleFeature(featureId, false)));
	}
}
